import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { type IconName, ModuleIcon } from '../components/icons';
import { isPortalUser } from '../components/PortalLayout';
import { Alert, Badge, Spinner } from '../components/ui';
import { type MessageKey, t } from '../i18n';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { errorMessage } from '../lib/errors';
import type { RequestListItem } from './projects/requests-data';
import { formatDateTime, type TicketListItem, statusTone } from './tickets/data';

// Lo que un colaborador viene a hacer, en el orden en que más lo hace.
const ACTIONS: Array<{ to: string; icon: IconName; title: MessageKey; text: MessageKey }> = [
  { to: '/tickets/nuevo?tipo=incidencia', icon: 'reportar', title: 'portal.accion_problema', text: 'portal.accion_problema_ayuda' },
  { to: '/tickets/nuevo?tipo=solicitud', icon: 'pedir', title: 'portal.accion_pedido', text: 'portal.accion_pedido_ayuda' },
  { to: '/proyectos/solicitudes/nueva', icon: 'proyectos', title: 'portal.accion_proyecto', text: 'portal.accion_proyecto_ayuda' },
  { to: '/usuarios/altas', icon: 'usuarios', title: 'portal.accion_equipo', text: 'portal.accion_equipo_ayuda' },
];

type Tab = 'abiertos' | 'terminado';

/**
 * Inicio de quien no atiende tickets: qué necesita (reportar, pedir, solicitar), lo que espera su aprobación y el
 * estado de sus tickets. En el portal la franja de bienvenida va a lo ancho; con el menú lateral, como un bloque.
 */
export function PortalHome() {
  const me = useMe();
  const navigate = useNavigate();
  const fullBleed = isPortalUser(me);
  const [search, setSearch] = useState('');
  const firstName = me.fullName.split(' ')[0] ?? me.fullName;

  const pending = useQuery({
    queryKey: ['solicitudes', 'lista', 'por_aprobar', 'abiertas'],
    queryFn: () => api<RequestListItem[]>('/api/proyectos/solicitudes?alcance=por_aprobar&estado=abiertas'),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const q = search.trim();
    // Un número de ticket (T-000123 o 123) va a sus tickets; lo demás se busca en las guías.
    if (/^(t-?)?\d+$/i.test(q)) navigate(`/tickets?${new URLSearchParams({ buscar: q })}`);
    else navigate(q ? `/conocimiento?${new URLSearchParams({ buscar: q })}` : '/conocimiento');
  }

  const container = fullBleed ? 'mx-auto max-w-6xl px-4 md:px-8' : '';
  return (
    <div className={fullBleed ? 'pb-10' : 'space-y-6'}>
      <section className={`bg-tinta text-white ${fullBleed ? '' : 'rounded-md'}`}>
        <div className={`${fullBleed ? 'mx-auto max-w-6xl px-4 pb-14 pt-10 md:px-8 md:pb-16 md:pt-14' : 'px-6 py-8 md:px-10'}`}>
          <p className="text-sm text-senal">{t('inicio.saludo', { nombre: firstName })}</p>
          <h1 className="mt-1 max-w-3xl text-3xl font-semibold leading-tight tracking-tight md:text-4xl">{t('portal.pregunta')}</h1>
          <form onSubmit={submit} role="search" className="mt-6 flex max-w-2xl gap-2">
            <label htmlFor="portal-buscar" className="sr-only">
              {t('portal.buscar')}
            </label>
            <input
              id="portal-buscar"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('portal.buscar')}
              className="min-w-0 flex-1 rounded border border-white/20 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-senal"
            />
            <button type="submit" className="rounded bg-senal px-5 py-3 text-sm font-semibold text-tinta hover:bg-amber-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
              {t('portal.buscar_boton')}
            </button>
          </form>
        </div>
      </section>

      <div className={`${container} ${fullBleed ? '-mt-8 space-y-6' : 'space-y-6'}`}>
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label={t('portal.acciones')}>
          {ACTIONS.map((a) => (
            <li key={a.to}>
              <Link
                to={a.to}
                className="group flex h-full gap-3 rounded-md border border-slate-200 bg-white p-4 transition-colors hover:border-marca focus:outline-none focus-visible:ring-2 focus-visible:ring-marca/40"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-marca-suave text-marca-claro group-hover:bg-marca group-hover:text-white">
                  <ModuleIcon name={a.icon} />
                </span>
                <span>
                  <span className="block font-medium text-slate-900">{t(a.title)}</span>
                  <span className="mt-0.5 block text-sm text-slate-600">{t(a.text)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>

        {!!pending.data?.length && (
          <Alert tone="warning">
            {t('portal.por_aprobar', { n: pending.data.length })}{' '}
            <Link to="/proyectos/solicitudes?alcance=por_aprobar" className="font-medium underline">
              {t('portal.revisar')}
            </Link>
          </Alert>
        )}

        <MyTickets />
      </div>
    </div>
  );
}

/** Sus tickets, en curso o resueltos. */
function MyTickets() {
  const [tab, setTab] = useState<Tab>('abiertos');
  const tickets = useQuery({
    queryKey: ['tickets', 'lista', 'propios', tab, 'portal'],
    queryFn: () => api<TicketListItem[]>(`/api/tickets?alcance=propios&estado=${tab}&limite=10`),
  });
  return (
    <section className="rounded-md border border-slate-200 bg-white" aria-label={t('portal.mis_tickets')}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5">
        <div role="tablist" aria-label={t('portal.mis_tickets')} className="flex gap-1">
          {(['abiertos', 'terminado'] as const).map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={tab === s}
              className={`-mb-px border-b-2 px-3 py-3 text-sm ${tab === s ? 'border-marca font-medium text-slate-900' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
              onClick={() => setTab(s)}
            >
              {t(s === 'abiertos' ? 'portal.en_curso' : 'portal.resueltos')}
            </button>
          ))}
        </div>
        <Link to="/tickets" className="py-3 text-sm text-marca hover:underline">
          {t('portal.ver_todos')}
        </Link>
      </div>
      <div className="p-5">
        {!tickets.data ? (
          tickets.error ? <Alert>{errorMessage(tickets.error)}</Alert> : <Spinner label={t('comun.cargando')} />
        ) : tickets.data.length === 0 ? (
          <p className="text-sm text-slate-500">{t(tab === 'abiertos' ? 'portal.sin_en_curso' : 'portal.sin_resueltos')}</p>
        ) : (
          <ul className="-my-2 divide-y divide-slate-100">
            {tickets.data.map((tk) => (
              <li key={tk.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5">
                <Link to={`/tickets/${tk.number}`} className="min-w-0 text-sm font-medium text-slate-900 hover:underline">
                  <span className="mr-2 font-mono text-xs font-normal text-slate-500">{tk.code}</span>
                  {tk.title}
                </Link>
                <span className="flex items-center gap-3 text-xs text-slate-500">
                  {t('portal.actualizado', { fecha: formatDateTime(tk.updatedAt) })}
                  <Badge tone={statusTone(tk.statusCategory)}>{tk.status}</Badge>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
